/**
 * Legacy Embed Compatibility Layer for Tours
 *
 * Scans the DOM for legacy storyscale/showpilot tour tags, attributes, and
 * classes and transforms them into standard `data-showpilot-tour` attributes
 * that the modern v3 widget expects.
 */

export function transformLegacyTours() {
  if (typeof document === 'undefined') return;

  // Helper to safely apply launcher attributes without overriding customer styles
  const applyLauncher = (el: Element, publicId: string) => {
    if (!el.hasAttribute('data-showpilot-tour')) {
      el.setAttribute('data-showpilot-tour', publicId);
    }
    if (!el.hasAttribute('data-showpilot-tour-mode')) {
      el.setAttribute('data-showpilot-tour-mode', 'launcher');
    }
    
    // Prevent boot.tsx from injecting the default `sp-tour-launch-btn` CSS classes
    // which would overwrite the customer's own button styles. By making the element 
    // its own trigger via an ID selector, boot.tsx just binds the click event cleanly.
    if (!el.hasAttribute('data-showpilot-tour-trigger')) {
      if (!el.id) {
        el.id = `sp-legacy-tour-${Math.random().toString(36).substring(2, 9)}`;
      }
      el.setAttribute('data-showpilot-tour-trigger', `#${el.id}`);
    }
  };

  const prefixes = ['storyscale', 'showpilot'];

  prefixes.forEach((prefix) => {
    // 1. Tour Embeds (e.g. <div storyscale-tour="UUID"></div>)
    const tourSelector = `[${prefix}-tour]`;
    const embedElements = document.querySelectorAll(tourSelector);
    embedElements.forEach((el) => {
      const publicId = el.getAttribute(`${prefix}-tour`)?.trim();
      if (publicId && !el.hasAttribute('data-showpilot-tour')) {
        el.setAttribute('data-showpilot-tour', publicId);
        if (!el.hasAttribute('data-showpilot-tour-mode')) {
          el.setAttribute('data-showpilot-tour-mode', 'embed');
        }
        if (!el.hasAttribute('data-showpilot-tour-autoplay')) {
          el.setAttribute('data-showpilot-tour-autoplay', 'true');
        }
      }
    });

    // 2. Tour Launchers (e.g. <button storyscale-tour-launcher="UUID">)
    const launcherAttrSelector = `[${prefix}-tour-launcher]`;
    const launcherElements = document.querySelectorAll(launcherAttrSelector);
    launcherElements.forEach((el) => {
      const publicId = el.getAttribute(`${prefix}-tour-launcher`)?.trim();
      if (publicId) applyLauncher(el, publicId);
    });

    // 3. Class-based Tour Launchers (e.g. class="btn storyscale-tour-launcher-UUID")
    const classSelector = `[class*="${prefix}-tour-launcher-"]`;
    const classElements = document.querySelectorAll(classSelector);
    classElements.forEach((el) => {
      if (el.hasAttribute('data-showpilot-tour')) return;
      
      const classPrefix = `${prefix}-tour-launcher-`;
      const classes = el.className.split(' ');
      const match = classes.find((c) => c.startsWith(classPrefix));
      
      if (match) {
        const publicId = match.substring(classPrefix.length).trim();
        if (publicId) {
          applyLauncher(el, publicId);
        }
      }
    });
  });

  // 4. URL-based Tour Launchers
  // Legacy share URLs were of the format: href="https://app.storyscale.com/share?launcher=true&clfn=tour&rptn=UUID"
  const urlLaunchers = document.querySelectorAll('a[href*="launcher=true"][href*="clfn=tour"][href*="rptn="]');
  urlLaunchers.forEach((el) => {
    if (el.hasAttribute('data-showpilot-tour')) return;

    try {
      const href = el.getAttribute('href');
      if (href) {
        // Handle relative or protocol-relative URLs if needed by using a dummy base
        const url = new URL(href, window.location.origin);
        const params = new URLSearchParams(url.search);
        
        if (params.get('launcher') === 'true' && params.get('clfn') === 'tour') {
          const publicId = params.get('rptn')?.trim();
          if (publicId) {
            applyLauncher(el, publicId);
          }
        }
      }
    } catch {
      // Malformed URL, ignore
    }
  });
}
